import axios, { AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import store from 'redux/store';
import { IDefaultSuccess, IDefaultFailed, IDefaultFinally, IApiOptions } from './type';
import {
  progressStart,
  progressEnd,
  redirect,
  enqueueSnackbar,
  logout,
} from 'core/redux/actions/lyaout/action';

import SETTINGS from 'core/settings';

const dispatch = store.dispatch;

const SETTINGS_REQUEST: {
  eventCheckUnauthorized: ((error: any) => boolean) | null;
  eventRefreshToken: (() => Promise<any>) | null;
} = {
  eventCheckUnauthorized: null,
  eventRefreshToken: null,
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (SETTINGS_REQUEST.eventCheckUnauthorized && SETTINGS_REQUEST.eventRefreshToken) {
      if (SETTINGS_REQUEST.eventCheckUnauthorized(error)) {
        const originalRequest = error.config;

        return SETTINGS_REQUEST.eventRefreshToken().then((res) => {
          if (res) {
            originalRequest.headers[SETTINGS.nameApiHeaderToken] = res?.token;
          }
          return axios(originalRequest);
        });
      }
    }
    return Promise.reject(error);
  }
);

const getApiUrl = (url: string, isWP = false): string => {
  let apiPath: string;

  if (isWP) {
    if (!SETTINGS.WPApiPath) {
      return url;
    }
    apiPath = SETTINGS.WPApiPath;
  } else {
    if (!SETTINGS.apiPath) {
      return url;
    }
    apiPath = SETTINGS.apiPath;
  }

  if (apiPath.slice(-1) !== '/') {
    apiPath = `${apiPath}/`;
  }

  if (url.slice(0, 1) === '/') {
    return `${apiPath}${url.slice(1)}`;
  }
  return `${apiPath}${url}`;
};

export const fetchAuthToken = (): string | undefined => Cookies.get(SETTINGS.authTokenStorage);

const getAuthHeaders = (): { 'X-AUTH-TOKEN': string } | {} => {
  const token = fetchAuthToken();
  if (token) {
    return {
      [SETTINGS.nameApiHeaderToken]: fetchAuthToken(),
    };
  }
  return {};
};

const defaultSuccess: IDefaultSuccess = (response, onSuccess, notify = false) => {
  if (notify) {
    dispatch(
      enqueueSnackbar({
        message: 'Success',
        options: {
          variant: 'success',
        },
      })
    );
  }
  if (onSuccess) {
    onSuccess(response.data);
  }
  return response;
};

const defaultFailed: IDefaultFailed = (error, onFailed, notify = false) => {
  let message;

  if (error.response) {
    const { status, data, config } = error.response;

    switch (status) {
      case 400:
        message = data ? data.label : '400 Bad Request';
        break;
      case 401:
        message = data ? data.label : '401 Unauthorized';
        dispatch(redirect(SETTINGS.pathLogin));
        dispatch(logout());
        break;
      // временно, до решения конфликтов с 403 ошибкой в AadhaarVerificationContainer
      // case 403:
      //   message = data ? data.label : '403 Forbidden';
      //   dispatch(redirect(SETTINGS.pathLogin));
      //   dispatch(logout());
      //   break;
      case 404:
        message = data ? data.label : '404 Not Found';
        break;
      case 429:
        message = data ? data.label : 'Too many Requests';
        break;
      case 500:
        message = data ? data.label : '500 Internal Server Error';
        break;
      default:
        message = `Error ${status} at ${config.url}`;
    }
  } else {
    message = 'Unidentified Error';
  }

  if (notify || error.response?.status === 500) {
    dispatch(
      enqueueSnackbar({
        message: message,
        options: {
          variant: 'error',
        },
      })
    );
  }
  if (onFailed) {
    if (error.response) {
      if (typeof error.response.data === 'string') {
        onFailed({
          data: {
            error_message: error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          },
        });
      } else {
        onFailed({
          data: {
            ...error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          },
        });
      }
    } else {
      onFailed({ data: error });
    }
  }
  throw error;
};

const defaultFinally: IDefaultFinally = (onFinally) => {
  if (onFinally) {
    onFinally();
  }
};

const request = ({
  method,
  url,
  data,
  headers = {},
  onSuccess,
  onFailed,
  onFinally,
  isModal = false,
  isAuth = true,
  isWP = false,
  notify = false,
  isShowLoader = true,
  responseType,
}: IApiOptions): Promise<any> => {
  isShowLoader && dispatch(progressStart(url, isModal));

  const config: AxiosRequestConfig = {
    url: getApiUrl(url, isWP),
    method: method,
    headers: headers,
    responseType: responseType,
  };

  if (['GET', 'get'].includes(method)) {
    config.params = data;
  } else {
    config.data = data;
  }

  if (isAuth) {
    config.headers = { ...headers, ...getAuthHeaders() };
  }

  return axios
    .request(config)
    .then((response) => {
      return defaultSuccess(response, onSuccess, notify);
    })
    .catch((error) => defaultFailed(error, onFailed, notify))
    .finally(() => {
      //if (!isModal) {
      isShowLoader && dispatch(progressEnd(url, isModal));
      //}
      defaultFinally(onFinally);
    });
};

const WebApi = { request, getApiUrl, getAuthHeaders, SETTINGS_REQUEST };

export default WebApi;
